import { gql, TypedDocumentNode } from "@apollo/client";

import { CAREER_INTENT_FIELDS, type CareerPlanRoleRubric } from "../queries";
import type { CareerPlanActionItem, CareerPlanGraphqlHighlight, CareerPlanStatus } from "./careerPlanDocument";
import type { CareerPlanAssessment, CareerPlanGoal } from "./careerPlanHistory";

export type CareerPlanDocument = {
  id: string;
  title: string | null;
  status: CareerPlanStatus;
  timeframe: string | null;
  careerTarget: string | null;
  additionalNotes: string | null;
  preparedById: string;
  preparedByName: string;
  completedAt: string | null;
  updatedAt: string | null;
  highlights: CareerPlanGraphqlHighlight[];
  actionItems: CareerPlanActionItem[];
};

export type GetPersonCareerPlansResponse = {
  person: {
    id: string;
    careerPlans: CareerPlanDocument[];
  } | null;
};

export type GetPersonCareerPlansVariables = {
  pgaId: string;
};

export const CAREER_PLAN_DOCUMENT_FIELDS = gql`
  fragment CareerPlanDocumentFields on BackofficeCareerPlan {
    id
    title
    status
    timeframe
    careerTarget
    additionalNotes
    preparedById
    preparedByName
    completedAt
    updatedAt
    highlights {
      id
      highlightType
      title
      description
    }
    actionItems {
      id
      focusArea
      recommendedActivity
      successMeasure
      timeframe
    }
  }
`;

export type GetCareerPlanLinkedTasksResponse = {
  tasks: {
    nodes: Array<{ id: string } | null> | null;
  } | null;
};

export type GetCareerPlanLinkedTasksVariables = {
  first: number;
  input: {
    referenceableId: string;
    referenceableType: string[];
  };
};

export const GET_CAREER_PLAN_LINKED_TASKS_QUERY: TypedDocumentNode<
  GetCareerPlanLinkedTasksResponse,
  GetCareerPlanLinkedTasksVariables
> = gql`
  query GetCareerPlanLinkedTasks($first: Int, $input: BackofficeCRMTasksInput) {
    tasks(first: $first, input: $input) {
      nodes {
        id
      }
    }
  }
`;

export const GET_PERSON_CAREER_PLANS_QUERY: TypedDocumentNode<
  GetPersonCareerPlansResponse,
  GetPersonCareerPlansVariables
> = gql`
  query GetPersonCareerPlans($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      careerPlans {
        id
        title
        status
        timeframe
        careerTarget
        additionalNotes
        preparedById
        preparedByName
        completedAt
        updatedAt
        highlights {
          id
          highlightType
          title
          description
        }
        actionItems {
          id
          focusArea
          recommendedActivity
          successMeasure
          timeframe
        }
      }
    }
  }
`;

export type GetPersonCareerPlanPdfResponse = {
  person: {
    id: string;
    careerPlan: {
      id: string;
      pdfUrl: string | null;
    } | null;
  } | null;
};

export type GetPersonCareerPlanPdfVariables = {
  pgaId: string;
  id: string;
};

export const GET_PERSON_CAREER_PLAN_PDF_QUERY: TypedDocumentNode<
  GetPersonCareerPlanPdfResponse,
  GetPersonCareerPlanPdfVariables
> = gql`
  query GetPersonCareerPlanPdf($pgaId: ID!, $id: ID!) {
    person(pgaId: $pgaId) {
      id
      careerPlan(id: $id) {
        id
        pdfUrl
      }
    }
  }
`;

export const CAREER_PLAN_ONBOARDING_INTERACTION_LIMIT = 10;

export type CareerPlanOnboardingInteraction = {
  id: string;
  interactionDate: string | null;
  mode: string;
  categories: string[];
  summary: string | null;
  relatedTo: string | null;
  owner: { name: string } | null;
  lastMessage: {
    content: string;
    isPrivate: boolean;
    haveAccess: boolean;
  };
};

export type GetCareerPlanOnboardingInteractionsResponse = {
  person: {
    id: string;
    interactions: {
      nodes: CareerPlanOnboardingInteraction[] | null;
    } | null;
  } | null;
};

export type GetCareerPlanOnboardingInteractionsVariables = {
  pgaId: string;
  first: number;
};

export const GET_CAREER_PLAN_ONBOARDING_INTERACTIONS_QUERY: TypedDocumentNode<
  GetCareerPlanOnboardingInteractionsResponse,
  GetCareerPlanOnboardingInteractionsVariables
> = gql`
  query GetCareerPlanOnboardingInteractions($pgaId: ID!, $first: Int!) {
    person(pgaId: $pgaId) {
      id
      interactions(search: $pgaId, first: $first, orderBy: { field: INTERACTION_DATE, direction: DESC }) {
        nodes {
          id
          interactionDate
          mode
          categories
          summary
          relatedTo
          owner {
            name
          }
          lastMessage {
            content
            isPrivate
            haveAccess
          }
        }
      }
    }
  }
`;

export type GetCareerPlanOnboardingGoalsResponse = {
  person: {
    id: string;
    careerIntent?: CareerPlanGoal | null;
    careerIntents: CareerPlanGoal[];
  } | null;
};

export type GetCareerPlanOnboardingGoalsVariables = {
  pgaId: string;
};

export const GET_CAREER_PLAN_ONBOARDING_GOALS_QUERY: TypedDocumentNode<
  GetCareerPlanOnboardingGoalsResponse,
  GetCareerPlanOnboardingGoalsVariables
> = gql`
  query GetCareerPlanOnboardingGoals($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      careerIntent {
        ...CareerIntentFields
      }
      careerIntents {
        ...CareerIntentFields
      }
    }
  }
  ${CAREER_INTENT_FIELDS}
`;

export type GetCareerPlanOnboardingExperienceIndexResponse = {
  careerRoleRubrics: CareerPlanRoleRubric[];
  person: {
    id: string;
    experienceIndexAssessment?: CareerPlanAssessment | null;
  } | null;
};

export type GetCareerPlanOnboardingExperienceIndexVariables = {
  pgaId: string;
};

export const GET_CAREER_PLAN_ONBOARDING_EXPERIENCE_INDEX_QUERY: TypedDocumentNode<
  GetCareerPlanOnboardingExperienceIndexResponse,
  GetCareerPlanOnboardingExperienceIndexVariables
> = gql`
  query GetCareerPlanOnboardingExperienceIndex($pgaId: ID!) {
    careerRoleRubrics(assessmentKind: EXPERIENCE) {
      id
      title
      classificationLabel
      competencyScores {
        competencyId
        competencyName
        desiredScore
      }
    }
    person(pgaId: $pgaId) {
      id
      experienceIndexAssessment {
        id
        answeredQuestionCount
        totalQuestionCount
        updatedAt
        assessmentComparisonRole
        scores {
          competencyId
          competencyName
          score
          answeredQuestionCount
          totalQuestionCount
          isComplete
        }
      }
    }
  }
`;
